import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Icon } from "./icons";

/** Interface text goes through the app's translator (set by the web app; English as it is until then). */
let translate: (en: string) => string = (en) => en;
export function setTranslator(f: (en: string) => string): void {
  translate = f;
}
export const tr = (en: string): string => translate(en);

export interface WhyLine {
  label: string;
  value: number | string;
  note?: string;
}

/**
 * The "why" behind a number: a small (i) button that opens a breakdown whose lines add up to the total.
 * Every score and number in the game gets one.
 */
export function Why({ title, lines, total, fmt = (v) => String(v), note, children }: {
  title: string;
  lines: WhyLine[];
  total?: number | string;
  fmt?: (v: number) => string;
  note?: string;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  useLayoutEffect(() => {
    if (!open || !btn.current || !pop.current) return;
    const r = btn.current.getBoundingClientRect();
    const pw = pop.current.offsetWidth;
    const ph = pop.current.offsetHeight;
    let left = Math.min(window.innerWidth - pw - 12, Math.max(12, r.left + r.width / 2 - pw / 2));
    let top = r.bottom + 8;
    if (top + ph > window.innerHeight - 12) top = Math.max(12, r.top - ph - 8);
    left = Math.max(12, left);
    setPos({ left, top });
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !pop.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", close);
    };
  }, [open]);
  return (
    <>
      <button ref={btn} className="why-btn" data-testid="why" aria-label={`Why: ${title}`} aria-expanded={open} onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>
        {children ?? <Icon name="info" size={16} stroke={2.2} />}
      </button>
      {open && (
        <div ref={pop} className="popover" role="dialog" aria-label={title} style={{ left: pos.left, top: pos.top }} data-testid="why-pop">
          <div className="caps" style={{ marginBottom: 6 }}>{title}</div>
          <table>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    {l.label}
                    {l.note ? <div className="muted" style={{ fontSize: "0.82rem" }}>{l.note}</div> : null}
                  </td>
                  <td className="v num">{typeof l.value === "number" ? fmt(l.value) : l.value}</td>
                </tr>
              ))}
              {total !== undefined && (
                <tr className="total">
                  <td>{tr("Total")}</td>
                  <td className="v num">{typeof total === "number" ? fmt(total) : total}</td>
                </tr>
              )}
            </tbody>
          </table>
          {note ? <div className="muted" style={{ marginTop: 8, fontSize: "0.82rem" }}>{note}</div> : null}
        </div>
      )}
    </>
  );
}

/** A number that counts up when it changes (doc: "numbers count up"). */
export function CountUp({ value, fmt = (v) => Math.round(v).toLocaleString("en-US"), ms = 400 }: { value: number; fmt?: (v: number) => string; ms?: number }) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const start = performance.now();
    const a = from.current;
    const b = value;
    if (a === b) return;
    let raf = 0;
    const tick = (t: number) => {
      const k = Math.min(1, (t - start) / ms);
      const v = a + (b - a) * (1 - Math.pow(1 - k, 3));
      setShown(v);
      if (k < 1) raf = requestAnimationFrame(tick);
      else from.current = b;
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      from.current = b;
    };
  }, [value, ms]);
  return <>{fmt(shown)}</>;
}

export function Meter({ value, color = "var(--coral)", style, label, glideMs }: { value: number; color?: string; style?: CSSProperties; label?: string; glideMs?: number }) {
  const v = Math.max(0, Math.min(1, value));
  return (
    <div className="meter" style={glideMs ? { ...style, ["--glide" as string]: `${glideMs}ms` } : style} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v * 100)} aria-label={label}>
      <i style={{ width: `${v * 100}%`, background: color }} />
    </div>
  );
}

export function Sparkline({ data, w = 120, h = 30, color = "var(--teal)", fill = true }: { data: number[]; w?: number; h?: number; color?: string; fill?: boolean }) {
  if (!data.length) return <svg width={w} height={h} />;
  const max = Math.max(1, ...data);
  const pts = data.map((v, i) => [data.length === 1 ? w / 2 : (i / (data.length - 1)) * w, h - 2 - (v / max) * (h - 4)] as const);
  const d = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} aria-hidden>
      {fill && <path d={`${d} L${w},${h} L0,${h} Z`} fill={color} opacity={0.15} />}
      <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
    </svg>
  );
}

/**
 * A sheet: its title and the close ×, nothing else above the content (docs/UI.md rule 1).
 * `badge` is one small icon chip beside the title (flask 43, coin $231M). `kicker` survives for old callers as a tiny
 * one-line caption that truncates; prefer a badge or nothing.
 */
export function Sheet({ title, kicker, badge, onClose, children, footer, wide, drawer, testId }: { title: ReactNode; kicker?: ReactNode; badge?: ReactNode; onClose?: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean; /** slides in from the right and leaves the studio in view (management screens) */ drawer?: boolean; testId?: string }) {
  // one stable listener: if it were re-added on every render, a re-render during the keypress would remove it
  // before the browser called it (listeners removed mid-dispatch are skipped), and Escape would do nothing
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const closable = !!onClose;
  useEffect(() => {
    if (!closable) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !document.querySelector(".popover")) closeRef.current?.();
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [closable]);
  return (
    <div className={drawer ? "scrim drawer" : "scrim"} onMouseDown={(e) => e.target === e.currentTarget && onClose?.()} data-testid={testId}>
      <section className={drawer ? `panel sheet drawer${wide ? " wide" : ""}` : "panel sheet"} role="dialog" aria-modal="true" style={wide && !drawer ? { width: "min(1180px, 100%)" } : undefined}>
        <header>
          <div style={{ flex: 1, minWidth: 0 }}>
            {kicker ? <div className="caps sheet-kicker" title={typeof kicker === "string" ? kicker : undefined}>{kicker}</div> : null}
            <div className="sheet-title">
              <h2 className="display">{title}</h2>
              {badge ? <span className="sheet-badge">{badge}</span> : null}
            </div>
          </div>
          {onClose ? (
            <button className="icon-btn" onClick={onClose} aria-label={tr("Close")}>
              <Icon name="close" />
            </button>
          ) : null}
        </header>
        <div className="body">{children}</div>
        {footer ? <footer>{footer}</footer> : null}
      </section>
    </div>
  );
}

export function Stat({ value, label, why, color }: { value: ReactNode; label: string; why?: ReactNode; color?: string }) {
  return (
    <div className="inset" style={{ padding: "8px 12px", minWidth: 0 }}>
      <div className="num" style={{ fontSize: "1.4rem", color, display: "flex", alignItems: "center", gap: 4 }}>
        {value}
        {why}
      </div>
      <div className="muted" style={{ fontSize: "0.82rem" }}>{label}</div>
    </div>
  );
}

/** One row of tabs; on a narrow screen the row scrolls sideways (never wraps), keeping the chosen tab in view. */
export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: string }[]; value: T; onChange: (t: T) => void }) {
  const row = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const r = row.current;
    const on = r?.querySelector<HTMLElement>(".tab.on");
    if (!r || !on || r.scrollWidth <= r.clientWidth) return;
    // scroll the row only (scrollIntoView would also move the sheet); .tabs is position: relative
    const left = on.offsetLeft;
    if (left < r.scrollLeft) r.scrollLeft = left - 8;
    else if (left + on.offsetWidth > r.scrollLeft + r.clientWidth) r.scrollLeft = left + on.offsetWidth - r.clientWidth + 8;
  }, [value]);
  return (
    <div className="tabs" role="tablist" ref={row}>
      {tabs.map((t) => (
        <button key={t.id} role="tab" aria-selected={t.id === value} className={`tab ${t.id === value ? "on" : ""}`} onClick={() => onChange(t.id)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}
